/* Wishlist page: renders the saved pieces (LMB.wishlist, assets/wishlist.js) as product cards. Each card comes
   from /products/<handle>?section_id=wishlist-card, six at a time, and each batch shows as soon as it arrives.
   Pieces that no longer exist are dropped from the list; a piece whose handle changed keeps its place under the new
   handle; pieces that fail to load (offline, server error) stay saved and a message says so.
   Removing a heart here takes the card off the page and moves focus to the next card's heart. */
if (!customElements.get('lmb-wishlist-page')) {
  customElements.define(
    'lmb-wishlist-page',
    class LmbWishlistPage extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.body = this.querySelector('[data-wishlist-body]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.error = this.querySelector('[data-wishlist-error]');
        this.count = this.querySelector('[data-wishlist-page-count]');
        this.status = this.querySelector('[data-wishlist-status]');
        this.failed = 0;
        this.onChange = () => this.syncRemovals();
        document.addEventListener('lmb:wishlist:change', this.onChange);
        this.load();
      }

      disconnectedCallback() {
        document.removeEventListener('lmb:wishlist:change', this.onChange);
      }

      get wishlist() {
        return window.LMB && LMB.wishlist;
      }

      handles() {
        return this.wishlist ? this.wishlist.handles() : [];
      }

      // Resolves to a card <li>, 'gone' (deleted or unpublished) or 'failed' (try again later).
      async fetchCard(handle) {
        try {
          const response = await fetch(`${this.dataset.cardUrl}${encodeURIComponent(handle)}?section_id=wishlist-card`);
          if (response.status === 404) return 'gone';
          if (!response.ok) return 'failed';
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cell = doc.querySelector('[data-wishlist-cell]');
          if (cell) return cell;
          return doc.querySelector('[data-wishlist-missing]') ? 'gone' : 'failed';
        } catch (error) {
          return 'failed';
        }
      }

      async load() {
        const handles = this.handles();
        const shown = new Set();
        for (let start = 0; start < handles.length; start += 6) {
          const batch = handles.slice(start, start + 6);
          const results = await Promise.all(batch.map((handle) => this.fetchCard(handle)));
          results.forEach((result, i) => {
            const handle = batch[i];
            if (result === 'gone') {
              if (this.wishlist) this.wishlist.remove(handle);
            } else if (result === 'failed') {
              this.failed += 1;
            } else {
              const actual = result.dataset.wishlistCell;
              if (actual !== handle && this.wishlist) this.wishlist.replace(handle, actual);
              if (shown.has(actual)) return;
              shown.add(actual);
              this.grid.append(result);
            }
          });
          this.ready();
        }
        this.ready();
        this.announce();
      }

      ready() {
        if (this.body.getAttribute('aria-busy') === 'false') return this.update();
        this.body.setAttribute('aria-busy', 'false');
        const loading = this.querySelector('[data-wishlist-loading]');
        if (loading) loading.remove();
        this.update();
      }

      syncRemovals() {
        const saved = new Set(this.handles());
        this.grid.querySelectorAll('[data-wishlist-cell]:not(.is-leaving)').forEach((cell) => {
          if (saved.has(cell.dataset.wishlistCell)) return;
          cell.classList.add('is-leaving');
          setTimeout(() => {
            // Saved again during the fade: keep it.
            if (this.wishlist && this.wishlist.has(cell.dataset.wishlistCell)) {
              cell.classList.remove('is-leaving');
              return;
            }
            const hadFocus = cell.contains(document.activeElement);
            const next = this.neighbour(cell);
            cell.remove();
            this.update();
            this.announce(true);
            if (hadFocus) {
              const target = next ? next.querySelector('[data-wishlist-toggle]') : this.querySelector('[data-wishlist-empty-link]');
              if (target) target.focus();
            }
          }, 200);
        });
      }

      neighbour(cell) {
        const isCell = (el) => el && el.matches('[data-wishlist-cell]:not(.is-leaving)');
        let el = cell.nextElementSibling;
        while (el && !isCell(el)) el = el.nextElementSibling;
        if (el) return el;
        el = cell.previousElementSibling;
        while (el && !isCell(el)) el = el.previousElementSibling;
        return el;
      }

      shownCount() {
        return this.grid.querySelectorAll('[data-wishlist-cell]').length;
      }

      update() {
        const n = this.shownCount();
        this.error.hidden = this.failed === 0;
        this.empty.hidden = n > 0 || this.failed > 0;
        const template = n === 1 ? this.dataset.countOne : this.dataset.countOther;
        this.count.textContent = n > 0 && template ? template.replace('[count]', n) : '';
      }

      announce(removed = false) {
        const n = this.shownCount();
        let text;
        if (this.failed > 0 && !removed) text = this.error.textContent.trim();
        else if (n === 0) text = this.dataset.statusEmpty;
        else text = (n === 1 ? this.dataset.statusOne : this.dataset.statusOther).replace('[count]', n);
        this.status.textContent = '';
        requestAnimationFrame(() => {
          this.status.textContent = text;
        });
      }
    }
  );
}
